<template>
  <div class="orderbook-page">
    <!-- Header -->
    <div class="ob-header">
      <div class="ob-header-left">
        <router-link :to="backRoute" class="ob-back-btn" title="Back to Chart">
          <i class="fa-solid fa-arrow-left"></i>
        </router-link>
        <div class="ob-symbol-info">
          <h1 class="ob-symbol">{{ displaySymbol }}</h1>
          <span class="ob-pair">/ USDT</span>
          <span class="ob-badge">SPOT</span>
          <span v-if="midPrice !== null" class="ob-header-price" :class="midPriceDirection">
            {{ formatPrice(midPrice) }}
          </span>
        </div>
      </div>

      <div class="ob-header-right">
        <div class="ob-status" :class="statusClass">
          <span class="ob-status-dot"></span>
          <span>{{ statusText }}</span>
        </div>
        <button v-if="!isStreaming" @click="startStream" class="ob-stream-btn start">
          <i class="fa-solid fa-play"></i> Stream
        </button>
        <button v-else @click="stopStream" class="ob-stream-btn stop">
          <i class="fa-solid fa-stop"></i> Stop
        </button>
      </div>
    </div>

    <!-- Orderbook Content -->
    <div class="ob-content">
      <!-- Active Orderbook Grid -->
      <div v-if="updateCount > 0" class="ob-card">
        <!-- Settings Backdrop (closes dropdown when clicking outside) -->
        <div v-if="showSettingsDropdown" class="ob-dropdown-backdrop" @click="showSettingsDropdown = false"></div>

        <!-- Orderbook Card Header -->
        <div class="ob-card-header">
          <div class="ob-card-header-left">
            <span class="ob-card-title">Order Book</span>
            <div class="ob-layout-switcher">
              <button 
                @click="settings.layoutMode = 'side-by-side'" 
                :class="{ active: settings.layoutMode === 'side-by-side' }"
                title="Bên cạnh nhau (Side-by-Side)"
              >
                <i class="fa-solid fa-table-columns"></i>
              </button>
              <button 
                @click="settings.layoutMode = 'bids'" 
                :class="{ active: settings.layoutMode === 'bids' }"
                title="Chỉ Mua (Bids Only)"
                class="btn-bids"
              >
                <i class="fa-solid fa-square text-green"></i>
              </button>
              <button 
                @click="settings.layoutMode = 'asks'" 
                :class="{ active: settings.layoutMode === 'asks' }"
                title="Chỉ Bán (Asks Only)"
                class="btn-asks"
              >
                <i class="fa-solid fa-square text-red"></i>
              </button>
            </div>
          </div>
          
          <div class="ob-card-header-right">
            <div class="ob-settings-wrapper">
              <button @click="showSettingsDropdown = !showSettingsDropdown" class="ob-settings-trigger" title="Tùy chỉnh">
                <i class="fa-solid fa-ellipsis"></i>
              </button>
              
              <!-- Settings Dropdown Popover -->
              <div v-if="showSettingsDropdown" class="ob-settings-dropdown">
                <div class="ob-settings-title-bar">
                  <span>Cài đặt sổ lệnh</span>
                  <i class="fa-solid fa-sliders"></i>
                </div>
                
                <div class="ob-settings-divider"></div>

                <div class="ob-settings-section">
                  <div class="ob-settings-section-title">Order Book Display</div>
                  
                  <label class="ob-control-label checkbox">
                    <input type="checkbox" v-model="settings.displayAvgSum" class="ob-hidden-input">
                    <span class="ob-custom-checkbox"><i class="fa-solid fa-check"></i></span>
                    <span class="ob-control-text">Display Avg. & Sum</span>
                  </label>
                  
                  <label class="ob-control-label checkbox">
                    <input type="checkbox" v-model="settings.showRatio" class="ob-hidden-input">
                    <span class="ob-custom-checkbox"><i class="fa-solid fa-check"></i></span>
                    <span class="ob-control-text">Show Buy/Sell Ratio</span>
                  </label>
                </div>
                
                <div class="ob-settings-divider"></div>
                
                <div class="ob-settings-section">
                  <div class="ob-settings-section-title">Book Depth Visualization</div>
                  
                  <label class="ob-control-label radio">
                    <input type="radio" value="amount" v-model="settings.depthType" class="ob-hidden-input">
                    <span class="ob-custom-radio"></span>
                    <span class="ob-control-text">Amount</span>
                  </label>
                  
                  <label class="ob-control-label radio">
                    <input type="radio" value="cumulative" v-model="settings.depthType" class="ob-hidden-input">
                    <span class="ob-custom-radio"></span>
                    <span class="ob-control-text">Cumulative</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Market Info Bar (Mid Price & Spread) -->
        <div class="ob-market-bar" v-if="midPrice !== null || spread !== null">
          <div class="ob-market-item" v-if="midPrice !== null">
            <span class="ob-market-label">Mid Price</span>
            <span class="ob-market-value mid-price" :class="midPriceDirection">
              <i :class="midPriceIcon"></i>
              {{ formatPrice(midPrice) }}
            </span>
            <span class="ob-mid-usd">≈ ${{ formatPrice(midPrice) }}</span>
          </div>
          <div class="ob-market-divider" v-if="midPrice !== null && spread !== null"></div>
          <div class="ob-market-item" v-if="spread !== null">
            <span class="ob-market-label">Spread</span>
            <span class="ob-market-value">{{ spread.toFixed(getPriceDecimals()) }}</span>
            <span class="ob-market-pct">({{ spreadPercent }}%)</span>
          </div>
        </div>

        <div class="ob-table-wrapper">
          <!-- Bids (buys) -->
          <div class="ob-side ob-bids" v-if="settings.layoutMode === 'side-by-side' || settings.layoutMode === 'bids'">
            <div class="ob-side-title green">Bids (Mua)</div>
            <div class="ob-table-header">
              <span class="ob-col ob-col-price">Price (USDT)</span>
              <span class="ob-col ob-col-amount">Amount ({{ displaySymbol }})</span>
              <span class="ob-col ob-col-total">Total (USDT)</span>
            </div>
            <div class="ob-rows">
              <div
                v-for="(bid, i) in bids"
                :key="'bid-' + i"
                class="ob-row ob-row-bid"
                @mouseenter="hoverIndex = i; hoverSide = 'bid'"
                @mouseleave="hoverIndex = null; hoverSide = null"
              >
                <div class="ob-depth-bar ob-depth-bid" :style="{ width: bidDepthPercent(bid, i) + '%' }"></div>
                <span class="ob-col ob-col-price ob-price-bid">{{ formatPrice(bid[0]) }}</span>
                <span class="ob-col ob-col-amount">{{ formatAmount(bid[1]) }}</span>
                <span class="ob-col ob-col-total">{{ formatTotal(bid[0], bid[1]) }}</span>

                <!-- Hover Tooltip -->
                <div v-if="settings.displayAvgSum && hoverIndex === i && hoverSide === 'bid'" class="ob-row-tooltip">
                  <div class="tooltip-item"><span>Avg:</span> <strong>{{ getHoverStats('bid', i).avgPrice }}</strong></div>
                  <div class="tooltip-item"><span>Sum:</span> <strong>{{ getHoverStats('bid', i).sumAmount }}</strong></div>
                  <div class="tooltip-item"><span>Total:</span> <strong>{{ getHoverStats('bid', i).sumTotal }}</strong></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div class="ob-table-divider" v-if="settings.layoutMode === 'side-by-side'"></div>

          <!-- Asks (sells) -->
          <div class="ob-side ob-asks" v-if="settings.layoutMode === 'side-by-side' || settings.layoutMode === 'asks'">
            <div class="ob-side-title red">Asks (Bán)</div>
            <div class="ob-table-header">
              <span class="ob-col ob-col-price">Price (USDT)</span>
              <span class="ob-col ob-col-amount">Amount ({{ displaySymbol }})</span>
              <span class="ob-col ob-col-total">Total (USDT)</span>
            </div>
            <div class="ob-rows">
              <div
                v-for="(ask, i) in asks"
                :key="'ask-' + i"
                class="ob-row ob-row-ask"
                @mouseenter="hoverIndex = i; hoverSide = 'ask'"
                @mouseleave="hoverIndex = null; hoverSide = null"
              >
                <div class="ob-depth-bar ob-depth-ask" :style="{ width: askDepthPercent(ask, i) + '%' }"></div>
                <span class="ob-col ob-col-price ob-price-ask">{{ formatPrice(ask[0]) }}</span>
                <span class="ob-col ob-col-amount">{{ formatAmount(ask[1]) }}</span>
                <span class="ob-col ob-col-total">{{ formatTotal(ask[0], ask[1]) }}</span>

                <!-- Hover Tooltip -->
                <div v-if="settings.displayAvgSum && hoverIndex === i && hoverSide === 'ask'" class="ob-row-tooltip">
                  <div class="tooltip-item"><span>Avg:</span> <strong>{{ getHoverStats('ask', i).avgPrice }}</strong></div>
                  <div class="tooltip-item"><span>Sum:</span> <strong>{{ getHoverStats('ask', i).sumAmount }}</strong></div>
                  <div class="tooltip-item"><span>Total:</span> <strong>{{ getHoverStats('ask', i).sumTotal }}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Depth Ratio Bar -->
        <div class="ob-ratio-bar" v-if="settings.showRatio && bids.length && asks.length">
          <div class="ob-ratio-bid" :style="{ width: bidRatio + '%' }">
            <span>B {{ bidRatio }}%</span>
          </div>
          <div class="ob-ratio-ask" :style="{ width: askRatio + '%' }">
            <span>S {{ askRatio }}%</span>
          </div>
        </div>
      </div>

      <!-- Loading State / Spinner -->
      <div v-else-if="isStreaming && !showTimeoutWarning" class="ob-loading">
        <div class="ob-spinner"></div>
        <p>Đang kết nối và chờ dữ liệu từ Binance Spot...</p>
        <p class="ob-loading-subtext">Stream: wss://stream.binance.com cho coin <strong>{{ displaySymbol }}</strong></p>
      </div>

      <!-- Timeout / Unsupported Symbol Warning -->
      <div v-else-if="isStreaming && showTimeoutWarning" class="ob-warning-card">
        <i class="fa-solid fa-circle-exclamation ob-warning-icon"></i>
        <h3 class="ob-warning-title">Không nhận được dữ liệu</h3>
        <p class="ob-warning-desc">
          Đồng coin <strong>{{ displaySymbol }}</strong> có thể không được hỗ trợ ở thị trường <strong>Spot</strong> của Binance.
        </p>
        <p class="ob-warning-sub">
          Vui lòng kiểm tra lại ký hiệu (symbol) hoặc quay lại biểu đồ.
        </p>
        <div class="ob-warning-actions">
          <router-link :to="backRoute" class="ob-warning-btn primary">
            <i class="fa-solid fa-arrow-left"></i> Quay lại biểu đồ
          </router-link>
          <button @click="startStream" class="ob-warning-btn secondary">
            <i class="fa-solid fa-rotate-right"></i> Thử lại
          </button>
        </div>
      </div>

      <!-- Disconnected / Stopped State -->
      <div v-else class="ob-empty">
        <i class="fa-solid fa-book-open"></i>
        <p>Nhấn nút <strong>Stream</strong> để bắt đầu nhận dữ liệu orderbook thời gian thực</p>
      </div>
    </div>

    <!-- Stats Footer -->
    <div class="ob-footer" v-if="lastUpdateId">
      <span>Update ID: {{ lastUpdateId }}</span>
      <span>Gói tin: {{ updateCount }}</span>
      <span>Độ trễ: {{ latency }}ms</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderbookView',
  data() {
    return {
      ws: null,
      isStreaming: false,
      status: 'disconnected', // 'disconnected' | 'connecting' | 'connected'
      bids: [],
      asks: [],
      lastUpdateId: null,
      prevMidPrice: null,
      midPriceDir: 'neutral', // 'up' | 'down' | 'neutral'
      updateCount: 0,
      latency: 0,
      lastMessageTime: 0,
      connectTimer: null,
      showTimeoutWarning: false,
      showSettingsDropdown: false,
      settings: {
        showRatio: true,
        depthType: 'amount', // 'amount' | 'cumulative'
        layoutMode: 'side-by-side', // 'side-by-side' | 'bids' | 'asks'
        displayAvgSum: true
      },
      hoverIndex: null,
      hoverSide: null
    };
  },
  computed: {
    symbol() {
      return (this.$route.params.symbol || 'BTC').toUpperCase();
    },
    displaySymbol() {
      return this.symbol;
    },
    wsSymbol() {
      return this.symbol.toLowerCase() + 'usdt';
    },
    backRoute() {
      return { name: 'BinanceNewUiCoin', params: { coinName: this.symbol } };
    },
    reversedAsks() {
      return [...this.asks].reverse();
    },
    midPrice() {
      if (!this.bids.length || !this.asks.length) return null;
      const bestBid = parseFloat(this.bids[0][0]);
      const bestAsk = parseFloat(this.asks[0][0]);
      return (bestBid + bestAsk) / 2;
    },
    spread() {
      if (!this.bids.length || !this.asks.length) return null;
      return parseFloat(this.asks[0][0]) - parseFloat(this.bids[0][0]);
    },
    spreadPercent() {
      if (!this.spread || !this.bids.length) return '0.00';
      const bestBid = parseFloat(this.bids[0][0]);
      if (bestBid <= 0) return '0.00';
      return ((this.spread / bestBid) * 100).toFixed(3);
    },
    midPriceDirection() {
      return this.midPriceDir === 'up' ? 'mid-up' : this.midPriceDir === 'down' ? 'mid-down' : '';
    },
    midPriceIcon() {
      if (this.midPriceDir === 'up') return 'fa-solid fa-caret-up';
      if (this.midPriceDir === 'down') return 'fa-solid fa-caret-down';
      return '';
    },
    maxBidTotal() {
      if (!this.bids.length) return 1;
      return Math.max(...this.bids.map(b => parseFloat(b[0]) * parseFloat(b[1])));
    },
    maxAskTotal() {
      if (!this.asks.length) return 1;
      return Math.max(...this.asks.map(a => parseFloat(a[0]) * parseFloat(a[1])));
    },
    bidRatio() {
      if (!this.bids.length || !this.asks.length) return 50;
      const bidVol = this.bids.reduce((s, b) => s + parseFloat(b[1]), 0);
      const askVol = this.asks.reduce((s, a) => s + parseFloat(a[1]), 0);
      const total = bidVol + askVol;
      if (total === 0) return 50;
      return ((bidVol / total) * 100).toFixed(1);
    },
    askRatio() {
      return (100 - parseFloat(this.bidRatio)).toFixed(1);
    },
    statusText() {
      if (this.status === 'connected') return 'Live';
      if (this.status === 'connecting') return 'Connecting...';
      return 'Disconnected';
    },
    statusClass() {
      return 'ob-status-' + this.status;
    },
  },
  methods: {
    startStream() {
      if (this.ws) this.stopStream();
      this.status = 'connecting';
      this.showTimeoutWarning = false;

      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
      }

      const url = `wss://stream.binance.com:9443/ws/${this.wsSymbol}@depth20@100ms`;
      this.ws = new WebSocket(url);

      this.connectTimer = setTimeout(() => {
        if (this.isStreaming && this.updateCount === 0) {
          this.showTimeoutWarning = true;
        }
      }, 5000);

      this.ws.onopen = () => {
        this.isStreaming = true;
        this.status = 'connected';
        this.updateCount = 0;
      };

      this.ws.onmessage = (event) => {
        const now = Date.now();
        if (this.lastMessageTime) {
          this.latency = now - this.lastMessageTime;
        }
        this.lastMessageTime = now;

        try {
          const data = JSON.parse(event.data);
          this.lastUpdateId = data.lastUpdateId;

          // Track mid price direction
          if (data.bids && data.bids.length && data.asks && data.asks.length) {
            const newMid = (parseFloat(data.bids[0][0]) + parseFloat(data.asks[0][0])) / 2;
            if (this.prevMidPrice !== null) {
              if (newMid > this.prevMidPrice) this.midPriceDir = 'up';
              else if (newMid < this.prevMidPrice) this.midPriceDir = 'down';
              else this.midPriceDir = 'neutral';
            }
            this.prevMidPrice = newMid;
          }

          this.bids = data.bids || [];
          this.asks = data.asks || [];
          this.updateCount++;
          // Hide timeout warning as we are successfully getting data
          this.showTimeoutWarning = false;
        } catch (e) {
          console.error('Orderbook parse error:', e);
        }
      };

      this.ws.onerror = () => {
        this.status = 'disconnected';
      };

      this.ws.onclose = () => {
        this.isStreaming = false;
        this.status = 'disconnected';
        this.ws = null;
        if (this.connectTimer) {
          clearTimeout(this.connectTimer);
          this.connectTimer = null;
        }
      };
    },

    stopStream() {
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      this.isStreaming = false;
      this.status = 'disconnected';
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
        this.connectTimer = null;
      }
      this.showTimeoutWarning = false;
    },

    getPriceDecimals() {
      if (!this.bids.length) return 2;
      const price = this.bids[0][0];
      const parts = price.split('.');
      return parts.length > 1 ? parts[1].length : 0;
    },

    formatPrice(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return '-';
      const decimals = this.getPriceDecimals();
      return num.toFixed(decimals);
    },

    formatAmount(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return '-';
      if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
      if (num >= 1) return num.toFixed(2);
      return num.toPrecision(4);
    },

    formatTotal(price, amount) {
      const total = parseFloat(price) * parseFloat(amount);
      if (isNaN(total)) return '-';
      if (total >= 1000000) return (total / 1000000).toFixed(2) + 'M';
      if (total >= 1000) return (total / 1000).toFixed(2) + 'K';
      return total.toFixed(2);
    },

    bidDepthPercent(bid, index) {
      if (this.settings.depthType === 'amount') {
        const total = parseFloat(bid[0]) * parseFloat(bid[1]);
        return Math.min((total / this.maxBidTotal) * 100, 100);
      } else {
        let cumTotal = 0;
        for (let k = 0; k <= index; k++) {
          if (this.bids[k]) {
            cumTotal += parseFloat(this.bids[k][0]) * parseFloat(this.bids[k][1]);
          }
        }
        let totalSum = 0;
        for (let k = 0; k < this.bids.length; k++) {
          totalSum += parseFloat(this.bids[k][0]) * parseFloat(this.bids[k][1]);
        }
        if (totalSum === 0) return 0;
        return Math.min((cumTotal / totalSum) * 100, 100);
      }
    },

    askDepthPercent(ask, index) {
      if (this.settings.depthType === 'amount') {
        const total = parseFloat(ask[0]) * parseFloat(ask[1]);
        return Math.min((total / this.maxAskTotal) * 100, 100);
      } else {
        let cumTotal = 0;
        for (let k = 0; k <= index; k++) {
          if (this.asks[k]) {
            cumTotal += parseFloat(this.asks[k][0]) * parseFloat(this.asks[k][1]);
          }
        }
        let totalSum = 0;
        for (let k = 0; k < this.asks.length; k++) {
          totalSum += parseFloat(this.asks[k][0]) * parseFloat(this.asks[k][1]);
        }
        if (totalSum === 0) return 0;
        return Math.min((cumTotal / totalSum) * 100, 100);
      }
    },

    getHoverStats(side, index) {
      const list = side === 'bid' ? this.bids : this.asks;
      let sumAmount = 0;
      let sumTotal = 0;
      for (let k = 0; k <= index; k++) {
        if (list[k]) {
          const p = parseFloat(list[k][0]);
          const a = parseFloat(list[k][1]);
          sumAmount += a;
          sumTotal += p * a;
        }
      }
      const avgPrice = sumAmount > 0 ? sumTotal / sumAmount : 0;
      return {
        avgPrice: avgPrice.toFixed(this.getPriceDecimals()),
        sumAmount: this.formatAmount(sumAmount),
        sumTotal: this.formatTotal(sumTotal, 1)
      };
    },
  },
  mounted() {
    // Auto-start streaming when page loads
    this.startStream();
  },
  beforeUnmount() {
    this.stopStream();
  },
};
</script>

<style scoped>
/* Page Layout */
.orderbook-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #eaecef;
  min-height: 100vh;
}

/* ── Header ── */
.ob-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #2b3139;
}

.ob-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ob-back-btn {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #2b3139;
  border: none;
  color: #848e9c;
  text-decoration: none;
  transition: all 0.2s ease;
}
.ob-back-btn:hover {
  background: #363e47;
  color: #eaecef;
}

.ob-symbol-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ob-symbol {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #eaecef;
}

.ob-pair {
  font-size: 16px;
  color: #848e9c;
  font-weight: 500;
  margin-right: 8px;
}

.ob-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: #2b3139;
  color: #f0b90b; /* Binance Yellow */
  letter-spacing: 0.5px;
}

.ob-header-price {
  font-size: 20px;
  font-weight: 700;
  margin-left: 16px;
  font-family: 'SF Mono', 'Roboto Mono', Monaco, monospace;
}
.ob-header-price.mid-up {
  color: #0ecb81;
}
.ob-header-price.mid-down {
  color: #f6465d;
}

.ob-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ── Status Indicator ── */
.ob-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  background: #1e2329;
  border: 1px solid #2b3139;
}

.ob-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.ob-status-connected {
  color: #0ecb81;
}
.ob-status-connected .ob-status-dot {
  background: #0ecb81;
  box-shadow: 0 0 8px rgba(14, 203, 129, 0.6);
  animation: ob-pulse-green 1.5s infinite;
}

.ob-status-connecting {
  color: #f0b90b;
}
.ob-status-connecting .ob-status-dot {
  background: #f0b90b;
  animation: ob-pulse-yellow 1s infinite;
}

.ob-status-disconnected {
  color: #848e9c;
}
.ob-status-disconnected .ob-status-dot {
  background: #848e9c;
}

@keyframes ob-pulse-green {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@keyframes ob-pulse-yellow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ── Stream Buttons ── */
.ob-stream-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ob-stream-btn.start {
  background: #0ecb81;
  color: #1e2329;
}
.ob-stream-btn.start:hover {
  background: #0fdb8b;
}

.ob-stream-btn.stop {
  background: rgba(246, 70, 93, 0.15);
  color: #f6465d;
  border: 1px solid rgba(246, 70, 93, 0.3);
}
.ob-stream-btn.stop:hover {
  background: rgba(246, 70, 93, 0.25);
}

/* ── Card Container ── */
.ob-card {
  background: #161a1e; /* Binance dark container */
  border: 1px solid #2b3139;
  border-radius: 8px;
  padding: 16px;
}

/* ── Market Info Bar ── */
.ob-market-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  padding: 12px 16px;
  background: #1e2329;
  border-radius: 4px;
  margin-bottom: 16px;
  border: 1px solid #2b3139;
}

.ob-market-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.ob-market-label {
  color: #848e9c;
  font-weight: 500;
}

.ob-market-value {
  font-weight: 700;
  font-family: 'SF Mono', 'Roboto Mono', monospace;
}
.ob-market-value.mid-up {
  color: #0ecb81;
}
.ob-market-value.mid-down {
  color: #f6465d;
}

.ob-mid-usd {
  font-size: 12px;
  color: #848e9c;
  margin-left: 4px;
}

.ob-market-pct {
  color: #848e9c;
  font-size: 12px;
}

.ob-market-divider {
  width: 1px;
  height: 16px;
  background: #2b3139;
}

/* ── Table side-by-side ── */
.ob-table-wrapper {
  display: flex;
  gap: 24px;
  margin-top: 10px;
}

.ob-table-divider {
  width: 1px;
  background: #2b3139;
  align-self: stretch;
}

@media (max-width: 640px) {
  .ob-table-wrapper {
    flex-direction: column;
    gap: 16px;
  }
  .ob-table-divider {
    width: 100%;
    height: 1px;
    margin: 8px 0;
    background: #2b3139;
  }
}

.ob-side {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* ── Column Titles ── */
.ob-side-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  text-align: center;
}
.ob-side-title.green {
  color: #0ecb81;
  background: rgba(14, 203, 129, 0.08);
}
.ob-side-title.red {
  color: #f6465d;
  background: rgba(246, 70, 93, 0.08);
}

.ob-table-header {
  display: flex;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #848e9c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #2b3139;
  margin-bottom: 6px;
}

.ob-col-price {
  flex: 1.2;
  text-align: left;
}
.ob-col-amount {
  flex: 1;
  text-align: right;
}
.ob-col-total {
  flex: 1;
  text-align: right;
}

/* ── Rows ── */
.ob-rows {
  max-height: 480px;
  overflow-y: auto;
}

.ob-row {
  display: flex;
  padding: 4px 10px;
  position: relative;
  transition: background 0.1s ease;
  cursor: pointer;
  align-items: center;
  height: 24px;
}
.ob-row:hover {
  background: #2b3139;
}
.ob-row span {
  font-family: 'SF Mono', 'Roboto Mono', Monaco, monospace;
  font-size: 12px;
  font-weight: 500;
}

.ob-depth-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0; /* Grow from right to left like Binance */
  pointer-events: none;
  transition: width 0.15s ease;
}

.ob-depth-bid {
  background: rgba(14, 203, 129, 0.08);
}

.ob-depth-ask {
  background: rgba(246, 70, 93, 0.08);
}

.ob-price-bid {
  color: #0ecb81;
}

.ob-price-ask {
  color: #f6465d;
}

.ob-col-amount,
.ob-col-total {
  color: #eaecef;
}

/* ── Ratio Bar ── */
.ob-ratio-bar {
  display: flex;
  height: 20px;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 16px;
  font-size: 11px;
  font-weight: 700;
}

.ob-ratio-bid {
  background: rgba(14, 203, 129, 0.2);
  color: #0ecb81;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.3s ease;
}

.ob-ratio-ask {
  background: rgba(246, 70, 93, 0.2);
  color: #f6465d;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.3s ease;
}

/* ── Loading, Empty, Warning States ── */
.ob-loading,
.ob-warning-card,
.ob-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  background: #161a1e;
  border: 1px solid #2b3139;
  border-radius: 8px;
  margin: 20px 0;
}

.ob-loading {
  color: #848e9c;
  gap: 16px;
}

.ob-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(240, 185, 11, 0.1);
  border-radius: 50%;
  border-top-color: #f0b90b; /* Binance Yellow */
  animation: ob-spin 1s linear infinite;
}

@keyframes ob-spin {
  to { transform: rotate(360deg); }
}

.ob-loading-subtext {
  font-size: 12px;
  color: #848e9c;
}

.ob-warning-card {
  border-color: rgba(240, 185, 11, 0.3);
  background: rgba(240, 185, 11, 0.02);
  gap: 16px;
}

.ob-warning-icon {
  font-size: 40px;
  color: #f0b90b;
}

.ob-warning-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #f0b90b;
}

.ob-warning-desc {
  font-size: 14px;
  color: #eaecef;
  margin: 0;
  line-height: 1.5;
}

.ob-warning-sub {
  font-size: 12px;
  color: #848e9c;
  margin: 0;
}

.ob-warning-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.ob-warning-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ob-warning-btn.primary {
  background: #f0b90b;
  color: #1e2329;
  border: none;
}
.ob-warning-btn.primary:hover {
  background: #f8cc1b;
}

.ob-warning-btn.secondary {
  background: #2b3139;
  color: #eaecef;
  border: 1px solid #363e47;
}
.ob-warning-btn.secondary:hover {
  background: #363e47;
}

.ob-empty {
  color: #848e9c;
  gap: 12px;
}
.ob-empty i {
  font-size: 36px;
  opacity: 0.5;
}

/* ── Footer ── */
.ob-footer {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #2b3139;
  font-size: 11px;
  color: #474f57;
}

/* ── Light Mode Overrides (Matches Binance Light) ── */
body:not(.dark-mode) .orderbook-page {
  color: #1e2329;
}
body:not(.dark-mode) .ob-symbol {
  color: #1e2329;
}
body:not(.dark-mode) .ob-back-btn {
  background: #eaecef;
  color: #707a8a;
}
body:not(.dark-mode) .ob-back-btn:hover {
  background: #d2d6dc;
  color: #1e2329;
}
body:not(.dark-mode) .ob-status {
  background: #ffffff;
  border-color: #eaecef;
  color: #707a8a;
}
body:not(.dark-mode) .ob-status-connected {
  color: #03a66d;
}
body:not(.dark-mode) .ob-status-connected .ob-status-dot {
  background: #03a66d;
}
body:not(.dark-mode) .ob-card {
  background: #ffffff;
  border-color: #eaecef;
}
body:not(.dark-mode) .ob-market-bar {
  background: #f6f6f6;
  border-color: #eaecef;
}
body:not(.dark-mode) .ob-market-label {
  color: #707a8a;
}
body:not(.dark-mode) .ob-market-value {
  color: #1e2329;
}
body:not(.dark-mode) .ob-market-value.mid-up {
  color: #03a66d;
}
body:not(.dark-mode) .ob-market-value.mid-down {
  color: #cf304a;
}
body:not(.dark-mode) .ob-market-divider {
  background: #eaecef;
}
body:not(.dark-mode) .ob-table-divider {
  background: #eaecef;
}
body:not(.dark-mode) .ob-side-title.green {
  color: #03a66d;
  background: rgba(3, 166, 109, 0.08);
}
body:not(.dark-mode) .ob-side-title.red {
  color: #cf304a;
  background: rgba(207, 48, 74, 0.08);
}
body:not(.dark-mode) .ob-table-header {
  color: #707a8a;
  border-bottom-color: #eaecef;
}
body:not(.dark-mode) .ob-row:hover {
  background: #f0f2f5;
}
body:not(.dark-mode) .ob-price-bid {
  color: #03a66d;
}
body:not(.dark-mode) .ob-price-ask {
  color: #cf304a;
}
body:not(.dark-mode) .ob-col-amount,
body:not(.dark-mode) .ob-col-total {
  color: #1e2329;
}
body:not(.dark-mode) .ob-depth-bid {
  background: rgba(3, 166, 109, 0.08);
}
body:not(.dark-mode) .ob-depth-ask {
  background: rgba(207, 48, 74, 0.08);
}
body:not(.dark-mode) .ob-ratio-bid {
  background: rgba(3, 166, 109, 0.2);
  color: #03a66d;
}
body:not(.dark-mode) .ob-ratio-ask {
  background: rgba(207, 48, 74, 0.2);
  color: #cf304a;
}
body:not(.dark-mode) .ob-loading,
body:not(.dark-mode) .ob-warning-card,
body:not(.dark-mode) .ob-empty {
  background: #ffffff;
  border-color: #eaecef;
}
body:not(.dark-mode) .ob-warning-card {
  border-color: rgba(240, 185, 11, 0.5);
  background: rgba(240, 185, 11, 0.04);
}
body:not(.dark-mode) .ob-warning-desc {
  color: #1e2329;
}
body:not(.dark-mode) .ob-warning-btn.secondary {
  background: #eaecef;
  color: #1e2329;
  border-color: #d2d6dc;
}
body:not(.dark-mode) .ob-warning-btn.secondary:hover {
  background: #d2d6dc;
}

/* ── Scrollbar ── */
.ob-rows::-webkit-scrollbar {
  width: 4px;
}
.ob-rows::-webkit-scrollbar-track {
  background: transparent;
}
.ob-rows::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}
body:not(.dark-mode) .ob-rows::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
}

/* ── Dropdown Backdrop ── */
.ob-dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  background: transparent;
}

/* ── Card Header ── */
.ob-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  border-bottom: 1px solid #2b3139;
  padding-bottom: 10px;
}
body:not(.dark-mode) .ob-card-header {
  border-bottom-color: #eaecef;
}

.ob-card-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ob-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #eaecef;
}
body:not(.dark-mode) .ob-card-title {
  color: #1e2329;
}

.ob-layout-switcher {
  display: flex;
  gap: 6px;
  background: #1e2329;
  padding: 3px;
  border-radius: 4px;
  border: 1px solid #2b3139;
}
body:not(.dark-mode) .ob-layout-switcher {
  background: #f6f6f6;
  border-color: #eaecef;
}

.ob-layout-switcher button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #848e9c;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s ease;
}
body:not(.dark-mode) .ob-layout-switcher button {
  color: #707a8a;
}

.ob-layout-switcher button:hover {
  color: #eaecef;
}
body:not(.dark-mode) .ob-layout-switcher button:hover {
  color: #1e2329;
}

.ob-layout-switcher button.active {
  background: #2b3139;
  color: #eaecef;
}
body:not(.dark-mode) .ob-layout-switcher button.active {
  background: #ffffff;
  color: #1e2329;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.ob-layout-switcher button.btn-bids i.text-green {
  color: rgba(14, 203, 129, 0.4);
}
.ob-layout-switcher button.btn-bids.active i.text-green,
.ob-layout-switcher button.btn-bids:hover i.text-green {
  color: #0ecb81;
}

.ob-layout-switcher button.btn-asks i.text-red {
  color: rgba(246, 70, 93, 0.4);
}
.ob-layout-switcher button.btn-asks.active i.text-red,
.ob-layout-switcher button.btn-asks:hover i.text-red {
  color: #f6465d;
}

/* ── Settings Dropdown ── */
.ob-settings-wrapper {
  position: relative;
}

.ob-settings-trigger {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #848e9c;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;
  z-index: 100;
  position: relative;
}
.ob-settings-trigger:hover {
  background: #2b3139;
  color: #eaecef;
}
body:not(.dark-mode) .ob-settings-trigger:hover {
  background: #f0f2f5;
  color: #1e2329;
}

.ob-settings-dropdown {
  position: absolute;
  top: 36px;
  right: 0;
  background: rgba(22, 26, 30, 0.95); /* Dark semi-transparent */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  width: 230px;
  padding: 16px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.5), 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ob-fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes ob-fade-in {
  from { opacity: 0; transform: translateY(-8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

body:not(.dark-mode) .ob-settings-dropdown {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.05);
}

.ob-settings-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: #eaecef;
  padding-bottom: 2px;
}
body:not(.dark-mode) .ob-settings-title-bar {
  color: #1e2329;
}

.ob-settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ob-settings-section-title {
  font-size: 10px;
  font-weight: 700;
  color: #848e9c;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}
body:not(.dark-mode) .ob-settings-section-title {
  color: #707a8a;
}

/* Custom Checkboxes and Radios */
.ob-hidden-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ob-control-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #eaecef;
  cursor: pointer;
  user-select: none;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background 0.15s ease;
}
.ob-control-label:hover {
  background: rgba(255, 255, 255, 0.04);
}
body:not(.dark-mode) .ob-control-label:hover {
  background: rgba(0, 0, 0, 0.04);
}
body:not(.dark-mode) .ob-control-label {
  color: #1e2329;
}

/* Custom Checkbox */
.ob-custom-checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #474f57;
  border-radius: 3px;
  background: #1e2329;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.ob-custom-checkbox i {
  font-size: 9px;
  color: transparent;
  transition: color 0.15s ease;
}
body:not(.dark-mode) .ob-custom-checkbox {
  background: #ffffff;
  border-color: #d2d6dc;
}

.ob-hidden-input:checked + .ob-custom-checkbox {
  background: #f0b90b; /* Binance Yellow */
  border-color: #f0b90b;
}
.ob-hidden-input:checked + .ob-custom-checkbox i {
  color: #1e2329; /* Dark checkmark */
}

/* Custom Radio */
.ob-custom-radio {
  width: 16px;
  height: 16px;
  border: 1px solid #474f57;
  border-radius: 50%;
  background: #1e2329;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  position: relative;
}
.ob-custom-radio::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  transition: background-color 0.15s ease;
}
body:not(.dark-mode) .ob-custom-radio {
  background: #ffffff;
  border-color: #d2d6dc;
}

.ob-hidden-input:checked + .ob-custom-radio {
  border-color: #f0b90b;
}
.ob-hidden-input:checked + .ob-custom-radio::after {
  background-color: #f0b90b;
}

.ob-control-text {
  font-weight: 500;
}

.ob-settings-divider {
  height: 1px;
  background: #2b3139;
}
body:not(.dark-mode) .ob-settings-divider {
  background: #eaecef;
}

/* ── Hover Tooltip ── */
.ob-row-tooltip {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 24px;
  background: #1e2329;
  border: 1px solid #474f57;
  border-radius: 4px;
  padding: 6px 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 50;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  pointer-events: none;
  font-size: 11px;
}
.ob-row-tooltip::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 4px 4px 0;
  border-style: solid;
  border-color: #1e2329 transparent transparent;
  display: block;
  width: 0;
}
.tooltip-item {
  display: flex;
  gap: 4px;
}
.tooltip-item span {
  color: #848e9c;
}
.tooltip-item strong {
  color: #eaecef;
}

/* Light mode overrides for tooltip */
body:not(.dark-mode) .ob-row-tooltip {
  background: #ffffff;
  border-color: #eaecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
body:not(.dark-mode) .ob-row-tooltip::after {
  border-color: #ffffff transparent transparent;
}
body:not(.dark-mode) .tooltip-item strong {
  color: #1e2329;
}
</style>
